import { ComponentChild, VNode, JSX } from 'preact';
import cn from 'classnames';
import { MutableRef, useCallback, useRef } from 'preact/hooks';

import { Typography } from '../Typography/Typography';

export interface OptionProps<T> {
  children?: ComponentChild;
  isSelected?: boolean;
  value: T;
  size: 'large' | 'small';
  onOptionClick: (param: { value: T; node: MutableRef<HTMLLIElement> }) => void;
}

export const Option = <T extends string | number>({
  children,
  isSelected = false,
  value,
  size,
  onOptionClick,
}: OptionProps<T>): VNode | null => {
  const ref = useRef<HTMLLIElement>(null!);

  const handleOptionClick = useCallback(() => {
    onOptionClick({ value, node: ref });
  }, [onOptionClick, value]);

  const handleKeyDown: JSX.KeyboardEventHandler<HTMLLIElement> = useCallback(
    (e) => {
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();

        handleOptionClick();
      }
    },
    [handleOptionClick]
  );

  return (
    <li
      ref={ref}
      aria-selected={isSelected}
      tabIndex={0}
      role="option"
      className={cn('vgp-picker-option', {
        'vgp-picker-option-size-small': size === 'small',
      })}
      onClick={handleOptionClick}
      onKeyDown={handleKeyDown}
    >
      <Typography variant="label" size="large" color="primary">
        {children}
      </Typography>
    </li>
  );
};
